/* global React, Icon */
/* global BASE_CATS, FIXED_REGIONS, adaptRecord, loadRegionRecords, stripHotspotDescriptionText, getLocalizedHotspotDescription, getLocalizedHotspotName, localizeCategoryLabel, useDismissOnOutsideTap */
const { useState: useStateDir, useEffect: useEffectDir, useRef: useRefDir } = React;

// Close-on-outside-tap for the menus lives in MapScreen.jsx, loaded before this file.
// If a stale cache serves MapScreen.js without it, the menus just stay open until
// closed by hand instead of the whole directory failing to render.
const useDirDismiss = typeof useDismissOnOutsideTap === 'function' ? useDismissOnOutsideTap : () => {};

const DIR_REGIONS = [{ id: 'all', label: 'All Maps' }, ...FIXED_REGIONS];

const REGION_LABEL = Object.fromEntries(FIXED_REGIONS.map(r => [r.id, r.label]));

// Back from a place's ficha may rebuild the page instead of restoring it from the
// back/forward cache. The filters and the list position the directory left on
// the history entry when it went to the ficha (see DirectoryScreen) bring the
// visitor back to where they were. Only on Back/Forward, never on a fresh visit.
function readDirReturn() {
  let saved = null;
  try {
    const navigation = performance.getEntriesByType('navigation')[0];
    saved = navigation && navigation.type === 'back_forward' && history.state && history.state.crmapsReturnDir;
  } catch (err) {}
  if (!saved || typeof saved !== 'object') return null;
  return {
    map: DIR_REGIONS.some(r => r.id === saved.map) ? saved.map : 'all',
    cat: BASE_CATS.some(c => c.id === saved.cat) ? saved.cat : 'all',
    search: typeof saved.search === 'string' ? saved.search : '',
    listTop: Math.max(0, Number(saved.listTop) || 0),
  };
}
// Used once: a later Back to the same entry must not bring back old filters.
function forgetDirReturn() {
  try {
    const state = history.state;
    if (!state || typeof state !== 'object' || !state.crmapsReturnDir) return;
    const { crmapsReturnDir, ...rest } = state;
    history.replaceState(rest, '');
  } catch (err) {}
}

function getRegionData(region) {
  return loadRegionRecords(region.id)
    .then(records => records.map((r, i) => ({ ...adaptRecord(r, i), region: region.label })))
    .catch(() => [])
    .then(hs =>
      hs.map(h => {
        const mapaId = (h._mapaId && h._mapaId !== '') ? h._mapaId : region.id;
        return { ...h, _mapaId: mapaId, region: REGION_LABEL[mapaId] || region.label };
      })
    );
}

function DirectoryScreen({ onCardClick, lang, onLanguageChange, copy }) {
  const [dirReturn] = useStateDir(readDirReturn);
  const [allHotspots, setAllHotspots] = useStateDir([]);
  const [loading,     setLoading]     = useStateDir(false);
  const [selectedMap, setSelectedMap] = useStateDir(dirReturn ? dirReturn.map : "all");
  const [catFilter,   setCatFilter]   = useStateDir(dirReturn ? dirReturn.cat : "all");
  const [searchQuery, setSearchQuery] = useStateDir(dirReturn ? dirReturn.search : '');
  const [mapDropOpen, setMapDropOpen] = useStateDir(false);
  const [catDropOpen, setCatDropOpen] = useStateDir(false);
  const listRef = useRefDir(null);
  const mapWrapRef  = useRefDir(null);
  const catsWrapRef = useRefDir(null);
  useDirDismiss(mapDropOpen || catDropOpen, [mapWrapRef, catsWrapRef], () => {
    setMapDropOpen(false);
    setCatDropOpen(false);
  });

  useEffectDir(() => {
    setLoading(true);
    Promise.all(FIXED_REGIONS.map(r => getRegionData(r)))
      .then(results => {
        const flat = results.flat().filter(h => h._catKey !== 'poi-promo');
        // Dedup per map: same business can appear in DU and MA but not twice within the same map.
        const seen = new Set();
        const deduped = flat.filter(h => {
          const name = (h.name || '').trim().toLowerCase();
          const key = name + '\x00' + (h._mapaId || '');
          if (!name || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setAllHotspots(deduped);
        setLoading(false);
      });
  }, []);

  // Back from a ficha that rebuilt the page: the filters came back with the
  // state above; the list position has to wait for the cards.
  useEffectDir(() => {
    if (!dirReturn || loading || allHotspots.length === 0) return;
    if (listRef.current) listRef.current.scrollTop = dirReturn.listTop;
  }, [allHotspots, loading]);

  // Read once on mount (or already live, when Back restores the page from the
  // back/forward cache): drop it from the entry either way.
  useEffectDir(() => {
    forgetDirReturn();
    const onShow = (e) => { if (e.persisted) forgetDirReturn(); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // Leaving for a place's ficha (openPlaceFicha, in HotspotPanel): leave the
  // filters and the list position on this history entry for readDirReturn.
  useEffectDir(() => {
    const onLeave = () => {
      try {
        const state = history.state && typeof history.state === 'object' ? history.state : {};
        history.replaceState({ ...state, crmapsReturnDir: {
          map: selectedMap, cat: catFilter, search: searchQuery,
          listTop: Math.round(listRef.current ? listRef.current.scrollTop : 0),
        } }, '');
      } catch (err) {}
    };
    window.addEventListener('crmaps:leaving-for-ficha', onLeave);
    return () => window.removeEventListener('crmaps:leaving-for-ficha', onLeave);
  }, [selectedMap, catFilter, searchQuery]);

  // For "all maps" view deduplicate by name so same business shows once.
  // For a specific map, show every entry belonging to that map (already deduped within-map above).
  let afterMapFilter;
  if (selectedMap === 'all') {
    const seenNames = new Set();
    afterMapFilter = allHotspots.filter(h => {
      const name = (h.name || '').trim().toLowerCase();
      if (!name || seenNames.has(name)) return false;
      seenNames.add(name);
      return true;
    });
  } else {
    afterMapFilter = allHotspots.filter(h => h._mapaId === selectedMap);
  }

  const afterCatFilter = catFilter === 'all'
    ? afterMapFilter
    : afterMapFilter.filter(h => h._catKey === catFilter);

  const q = searchQuery.trim().toLowerCase();
  const dirName = (h) => (getLocalizedHotspotName ? getLocalizedHotspotName(h, lang) : (h.name || ''));
  const visibleHotspots = (!q
    ? afterCatFilter
    : afterCatFilter.filter(h => {
        const name = dirName(h).toLowerCase();
        const region = (h.region || '').toLowerCase();
        return name.includes(q) || region.includes(q);
      }))
    .slice()
    .sort((a, b) => dirName(a).localeCompare(dirName(b), lang === 'es' ? 'es' : 'en', { sensitivity: 'base', numeric: true }));

  const activeCats = BASE_CATS.filter(c =>
    c.id === 'all' || afterMapFilter.some(h => h._catKey === c.id)
  );

  const mapLabel = selectedMap === 'all'
    ? (copy?.allMaps || 'All Maps')
    : (FIXED_REGIONS.find(r => r.id === selectedMap)?.label || copy?.allMaps || 'All Maps');

  return (
    <div className="dir-screen">

      {/* ── Toolbar: lang + map selector + cat filter ── */}
      <div className="dir-toolbar dir-toolbar--fixed">
        <div className="app-lang-toggle" aria-label="Language selector">
          <button type="button" className={lang === 'es' ? 'is-on' : ''} onClick={() => onLanguageChange?.('es')} aria-pressed={lang === 'es'}>ES</button>
          <button type="button" className={lang === 'en' ? 'is-on' : ''} onClick={() => onLanguageChange?.('en')} aria-pressed={lang === 'en'}>EN</button>
        </div>
        <div className="dir-map-wrap" ref={mapWrapRef}>
          <button
            className="map-search map-selector"
            aria-haspopup="listbox"
            aria-expanded={mapDropOpen}
            onClick={() => { setMapDropOpen(o => !o); setCatDropOpen(false); }}
          >
            {Icon.search}
            <span className="ms-label">{mapLabel}</span>
            <span className={'ms-chevron' + (mapDropOpen ? ' is-open' : '')}>{Icon.chevDown}</span>
          </button>

          {mapDropOpen && (
            <div className="map-dropdown dir-map-dropdown" role="listbox" aria-label={copy?.mapSelector || 'Map selector'}>
              {DIR_REGIONS.map(r => (
                <button
                  key={r.id}
                  role="option"
                  aria-selected={r.id === selectedMap}
                  className={'map-dropdown-item' + (r.id === selectedMap ? ' is-selected' : '')}
                  onClick={() => { setSelectedMap(r.id); setMapDropOpen(false); setCatFilter('all'); }}
                >
                  <span>{r.id === 'all' ? (copy?.allMaps || r.label) : r.label}</span>
                  {r.id === selectedMap && <span className="mdi-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="map-cats-wrap" ref={catsWrapRef}>
          <button
            className="map-iconbtn"
            aria-label={copy?.filterCategory || 'Filter by category'}
            aria-haspopup="listbox"
            aria-expanded={catDropOpen}
            onClick={() => { setCatDropOpen(o => !o); setMapDropOpen(false); }}
          >
            {Icon.layers}
          </button>

          {catDropOpen && (
            <div className="map-cats-dropdown" role="listbox" aria-label={copy?.categorySelector || 'Category selector'}>
              {activeCats.map(cat => (
                <button
                  key={cat.id}
                  role="option"
                  aria-selected={cat.id === catFilter}
                  className={'map-cats-item' + (cat.id === catFilter ? ' is-selected' : '')}
                  onClick={() => { setCatFilter(cat.id); setCatDropOpen(false); }}
                >
                  <span className="mci-left">
                    <img src={`assets/icons/${cat.icon}`} alt="" />
                    <span>{localizeCategoryLabel ? localizeCategoryLabel(cat.id, cat.label, lang) : cat.label}</span>
                  </span>
                  {cat.id === catFilter && <span className="mdi-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="dir-search-strip">
        <div className="dir-search">
          {Icon.search}
          <input
            type="search"
            placeholder={copy?.search || 'Search'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none',
                     font: 'inherit', color: 'inherit', minWidth: 0, WebkitAppearance: 'none' }}
          />
        </div>
      </div>

      {/* ── Scrollable cards ── */}
      <div className="dir-list" ref={listRef}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '32px 0',
                        font: '600 13px/1 var(--font-sans)', color: 'var(--fg-4)' }}>
            {copy?.loading || 'Loading...'}
          </div>
        )}
        {!loading && visibleHotspots.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 0',
                        font: '600 13px/1 var(--font-sans)', color: 'var(--fg-4)' }}>
            {copy?.empty || 'No places found.'}
          </div>
        )}
        <div className="dir-cards">
          {visibleHotspots.map((h) => {
            const displayName = getLocalizedHotspotName
              ? getLocalizedHotspotName(h, lang)
              : h.name;
            const localizedDesc = getLocalizedHotspotDescription
              ? getLocalizedHotspotDescription(h, lang)
              : h.desc;
            const descText = stripHotspotDescriptionText
              ? stripHotspotDescriptionText(localizedDesc)
              : String(localizedDesc || '');
            return (
              <div key={h._mapaId + '-' + h.id} className="dir-card" onClick={() => onCardClick(h)}>
                <div
                  className="dir-card-img"
                  style={{ backgroundImage: h.hero ? `url(${h.hero})` : 'none' }}
                />
                <div className="dir-card-body">
                  <div className="dir-card-badge">
                    <img src={`assets/icons/${h.icon}`} alt="" />
                    <span>{localizeCategoryLabel ? localizeCategoryLabel(h._catKey, h.catBadge, lang) : h.catBadge}</span>
                  </div>
                  <div className="dir-card-name">{displayName || h.name}</div>
                  <div className="dir-card-desc">{descText}</div>
                  <div className="dir-card-meta">
                    {h.discount && <span className="badge">{copy?.pass || 'Pass'}</span>}
                    {h.region && <span>{h.region}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

Object.assign(window, { DirectoryScreen });
